import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (matched to legacy admin's POSTs):
//
//   POST /admin/list_purpose    { limit, page, search?, sortyBy?, sortDirection? }
//                              → { success, data: { docs: [{_id, purpose, purposekey, ...}], totalDocs, ... }, message }
//
//   POST /admin/add_purpose     { purpose, S3Response }
//                              → { success, data, message }
//
//   POST /admin/update_purpose  { id, purpose }
//                              → { success, message }
//                              NOTE: legacy update payload is NAME ONLY —
//                              it does NOT support changing the image.
//                              We mirror this; the edit form only edits name.
//
//   POST /admin/delete_purpose  { id }
//                              → { success, message }
//
// NOTE 1: backend expects the typo `sortyBy` (extra "y"). Preserved.
// NOTE 2: capital 'S' in 'S3Response' on add. Preserved.
// NOTE 3: 'S3Response' wraps the ENTIRE upload response object
//         ({ s3response, file_name, s3_30sec_data? }), not just s3response.
//         CoverImageResult shape is compatible.
// ───────────────────────────────────────────────────────────────────────

/** Shape used by the dropdown helper (videos / chapters forms). Compact
 *  superset of what the list endpoint returns. */
export type PurposeOption = {
  _id: string
  purpose: string
  purposekey?: string
  s3response?: unknown
  created_at?: string
  updated_at?: string
}

const PURPOSE_FETCH_LIMIT = 200

/**
 * Used as a dropdown source by the Video / Chapter forms. Legacy fetched
 * limit:10; we bump to 200 because (a) datasets are small and (b) the
 * dropdown isn't paginated in either old or new admin, so showing only
 * 10 was a UX bug. Bumping doesn't change the API contract — just the
 * request value.
 */
export function usePurposes() {
  return useQuery({
    queryKey: ["admin", "purposes", "all"] as const,
    queryFn: async (): Promise<PurposeOption[]> => {
      const res = await api.post<{
        success: boolean
        data: { docs?: PurposeOption[] }
        message: string
      }>("/admin/list_purpose", {
        limit: PURPOSE_FETCH_LIMIT,
        page: 1,
        search: "",
      })
      const docs = res.data?.data?.docs
      return Array.isArray(docs) ? docs : []
    },
    staleTime: 5 * 60_000, // purposes change rarely
  })
}

// ───────────────────────────────────────────────────────────────────────
// Paginated list (for the Purposes management page).
// ───────────────────────────────────────────────────────────────────────
type PurposeListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type PurposeListResponse = {
  docs: PurposeOption[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

export function usePurposesList(params: PurposeListParams) {
  return useQuery({
    queryKey: ["admin", "purposes", "list", params] as const,
    queryFn: async (): Promise<PurposeListResponse> => {
      const res = await api.post<{
        success: boolean
        data: PurposeListResponse
        message: string
      }>("/admin/list_purpose", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

// ───────────────────────────────────────────────────────────────────────
// GET (single) — legacy doesn't have a dedicated /purpose_get endpoint.
// We pull from the all-purposes cache and find by id. Cheap because the
// usePurposes() query is cached for 5 minutes.
// ───────────────────────────────────────────────────────────────────────
export function useGetPurpose(id: string | undefined) {
  const allQ = usePurposes()
  return {
    isLoading: allQ.isLoading,
    isError: allQ.isError,
    data: id ? allQ.data?.find((p) => p._id === id) ?? null : null,
  }
}

// ───────────────────────────────────────────────────────────────────────
// CREATE — POST /admin/add_purpose
//   Body: { purpose, S3Response }
//
// S3Response is the FULL upload response object (s3response + file_name +
// s3_30sec_data). CoverImageResult has the same fields.
// ───────────────────────────────────────────────────────────────────────
type CreatePurposePayload = {
  purpose: string
  S3Response: {
    s3response: unknown
    file_name: string
    s3_30sec_data?: unknown
  }
}

export function useCreatePurpose() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreatePurposePayload) => {
      const res = await api.post<{
        success: boolean
        data?: PurposeOption
        message: string
      }>("/admin/add_purpose", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "purposes"] })
    },
  })
}

// ───────────────────────────────────────────────────────────────────────
// UPDATE — POST /admin/update_purpose
//   Body: { id, purpose }    (NAME ONLY — image change not supported)
// ───────────────────────────────────────────────────────────────────────
type UpdatePurposePayload = {
  id: string
  purpose: string
}

export function useUpdatePurpose() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdatePurposePayload) => {
      const res = await api.post<{
        success: boolean
        data?: PurposeOption
        message: string
      }>("/admin/update_purpose", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "purposes"] })
    },
  })
}

export function useDeletePurpose() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/admin/delete_purpose", { id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "purposes"] })
    },
  })
}
